// skills/alchemy/alchemy.js
// Alchemy skill - herb cleaning and potion brewing

import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import { addXP } from '../../systems/experience.js';
import { HERB_CLEANING, POTION_RECIPES, MORTARS } from './alchemyData.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { rollTalentProc } from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

// Herb tier order for Transmutation talent (clean herb outputs, low to high)
const HERB_TIER_ORDER = [
    'greenleaf', 'duskleaf', 'ironbloom', 'emberroot', 'sanctum_leaf',
    'sunpetal', 'deepvine', 'stonebark', 'brightmaw', 'nightvine',
    'glowstem', 'ashbloom', 'crownwort'
];

/**
 * Get the next tier herb for Transmutation talent
 * @param {string} currentHerb - The clean herb just produced
 * @returns {string|null} - The next tier clean herb, or null if already max tier
 */
function getNextTierHerb(currentHerb) {
    const currentIndex = HERB_TIER_ORDER.indexOf(currentHerb);
    if (currentIndex === -1 || currentIndex >= HERB_TIER_ORDER.length - 1) {
        return null; // Not found or already max tier
    }
    return HERB_TIER_ORDER[currentIndex + 1];
}

class AlchemySkill extends BaseSkill {
    constructor() {
        super(SKILLS.ALCHEMY, MORTARS, 'BASIC');
        this.onCleaningComplete = null;
        this.onBrewingComplete = null;
    }

    /**
     * Get current skill level
     */
    get level() {
        return this.getLevel();
    }

    /**
     * Get current equipped mortar (alias for BaseSkill method)
     */
    get currentMortar() {
        return this.getCurrentTool();
    }

    /**
     * Upgrade to a new mortar
     */
    upgradeMortar(mortarId) {
        const mortar = Object.values(MORTARS).find(m => m.id === mortarId);
        if (!mortar) return { success: false, reason: 'Invalid mortar' };

        if (this.level < mortar.level) {
            return { success: false, reason: `Requires level ${mortar.level}` };
        }

        if (gameState.hasPurchasedTool(SKILLS.ALCHEMY, mortarId)) {
            gameState.equipTool(SKILLS.ALCHEMY, mortarId);
            return { success: true, equipped: true };
        }

        const currentGold = gameState.getGold();
        if (currentGold < mortar.price) {
            return { success: false, reason: 'Not enough gold' };
        }

        const purchased = gameState.purchaseTool(SKILLS.ALCHEMY, mortarId, mortar.price);
        if (!purchased.success) {
            return purchased;
        }

        gameState.equipTool(SKILLS.ALCHEMY, mortarId);
        return { success: true, purchased: true };
    }

    // Check if player can clean a specific herb
    // Returns herbSource: 'inventory' | 'bloomhold' to indicate where grimy herbs are
    canCleanHerb(herbId) {
        const recipe = Object.values(HERB_CLEANING).find(h => h.id === herbId);
        if (!recipe) return { canClean: false, reason: 'Invalid herb' };

        if (this.level < recipe.level) {
            return { canClean: false, reason: `Requires level ${recipe.level}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { canClean: false, reason: 'Inventory full' };
        }

        // Check inventory first, then BloomHold
        const inventoryCount = gameState.getItemCount(recipe.inputItem);
        if (inventoryCount >= recipe.inputQuantity) {
            return { canClean: true, herbSource: 'inventory' };
        }

        // Check BloomHold if unlocked
        if (gameState.isBloomholdUnlocked()) {
            const bloomholdCount = gameState.getBloomholdItemCount(recipe.inputItem);
            if (bloomholdCount >= recipe.inputQuantity) {
                return { canClean: true, herbSource: 'bloomhold' };
            }
            // Show combined count in error
            const totalCount = inventoryCount + bloomholdCount;
            return { canClean: false, reason: `Need ${recipe.inputQuantity} ${recipe.inputItem.replace(/_/g, ' ')} (have ${totalCount})` };
        }

        return { canClean: false, reason: `Need ${recipe.inputQuantity} ${recipe.inputItem.replace(/_/g, ' ')}` };
    }

    // Start cleaning a herb
    startCleaningHerb(herbId, showToast = true) {
        const cleanResult = this.canCleanHerb(herbId);
        if (!cleanResult.canClean) {
            if (showToast) toastWarning(cleanResult.reason);
            return false;
        }

        const recipe = Object.values(HERB_CLEANING).find(h => h.id === herbId);
        const speedMultiplier = this.getEffectiveSpeedMultiplier({ subType: 'cleaning' });
        const adjustedTime = recipe.baseTime / speedMultiplier;
        const herbSource = cleanResult.herbSource;

        // Capture reference to skill for use in tick function
        const skill = this;

        const action = {
            type: SKILLS.ALCHEMY,
            subType: 'cleaning',
            name: `Cleaning ${recipe.name}`,
            recipe: recipe,
            herbSource: herbSource,
            progress: 0,
            duration: adjustedTime,
            startTime: Date.now(),
            forceComplete: false,
            tick: function(gameSpeedMultiplier = 1) {
                // Handle force complete from admin panel
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeCleaningHerb(recipe, this.herbSource);
                    // Try to restart if we have materials (no toast on auto-restart)
                    const { canClean } = skill.canCleanHerb(herbId);
                    if (canClean) {
                        skill.startCleaningHerb(herbId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                // Complete when progress reaches 1
                if (this.progress >= 1) {
                    skill.completeCleaningHerb(recipe, this.herbSource);
                    // Try to restart if we have materials (no toast on auto-restart)
                    const { canClean } = skill.canCleanHerb(herbId);
                    if (canClean) {
                        skill.startCleaningHerb(herbId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        logger.info(`Started cleaning ${recipe.name}${herbSource === 'bloomhold' ? ' (from BloomHold)' : ''}`);
        return true;
    }

    // Complete cleaning a herb
    completeCleaningHerb(recipe, herbSource = 'inventory') {
        // Get output item ID for proc notifications
        const outputItemId = recipe.drops[0]?.itemId;

        // Check for herb save proc (Careful Hands talent)
        const herbSaved = rollTalentProc(SKILLS.ALCHEMY, 'herbSave');

        if (!herbSaved) {
            // Consume input from the appropriate source
            let removed = false;
            if (herbSource === 'bloomhold') {
                removed = gameState.removeBloomholdItem(recipe.inputItem, recipe.inputQuantity);
            } else {
                removed = gameState.removeItem(recipe.inputItem, recipe.inputQuantity);
            }

            if (!removed) {
                logger.warn('Failed to remove grimy herb');
                this.stopAction();
                return;
            }
        } else {
            recordTalentProc(SKILLS.ALCHEMY, 'Careful Hands', 'Herb preserved!', null);
        }

        // Award XP (addXP handles admin multiplier internally)
        const xpResult = addXP(SKILLS.ALCHEMY, recipe.xp, recipe.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.ALCHEMY, recipe.drops);

        // Check for transmutation proc (chance to find bonus higher-tier herb, or extra of same if max tier)
        if (rollTalentProc(SKILLS.ALCHEMY, 'transmutation')) {
            const cleanHerb = recipe.drops[0]?.itemId;
            const nextTierHerb = getNextTierHerb(cleanHerb);
            if (nextTierHerb) {
                gameState.addItem(nextTierHerb, 1);
                recordTalentProc(SKILLS.ALCHEMY, 'Transmutation', null, null, { itemId: nextTierHerb, quantity: 1 });
            } else if (cleanHerb) {
                // Max tier herb - give an extra of the same herb
                gameState.addItem(cleanHerb, 1);
                recordTalentProc(SKILLS.ALCHEMY, 'Transmutation', null, null, { itemId: cleanHerb, quantity: 1 });
            }
        }

        // Check for 5x herbs proc (Botanical Genius capstone)
        if (rollTalentProc(SKILLS.ALCHEMY, 'fiveXHerbs')) {
            // Award 4 more drops (already got 1, total = 5)
            for (let i = 0; i < 4; i++) {
                awardDrops(SKILLS.ALCHEMY, recipe.drops);
            }
            if (outputItemId) {
                recordTalentProc(SKILLS.ALCHEMY, 'Botanical Genius', null, null, { itemId: outputItemId, quantity: 5 });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.ALCHEMY);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('alchemy');

        logger.game(`Cleaned ${recipe.name}! +${recipe.xp} XP${herbSource === 'bloomhold' ? ' (from BloomHold)' : ''}`);

        if (this.onCleaningComplete) {
            this.onCleaningComplete(recipe, xpResult);
        }
    }

    // Check if player can brew a specific potion
    // Returns herbSource and vialSource: 'inventory' | 'bloomhold' to indicate where ingredients are
    canBrewPotion(potionId) {
        const recipe = Object.values(POTION_RECIPES).find(p => p.id === potionId);
        if (!recipe) return { canBrew: false, reason: 'Invalid potion' };

        if (this.level < recipe.level) {
            return { canBrew: false, reason: `Requires level ${recipe.level}` };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { canBrew: false, reason: 'Inventory full' };
        }

        // Check for primary ingredient (clean herb) - inventory first, then BloomHold
        let herbSource = 'inventory';
        const inventoryHerbCount = gameState.getItemCount(recipe.inputItem);
        const bloomholdHerbCount = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(recipe.inputItem) : 0;

        if (inventoryHerbCount >= recipe.inputQuantity) {
            herbSource = 'inventory';
        } else if (bloomholdHerbCount >= recipe.inputQuantity) {
            herbSource = 'bloomhold';
        } else {
            const totalCount = inventoryHerbCount + bloomholdHerbCount;
            return { canBrew: false, reason: `Need ${recipe.inputQuantity} ${recipe.inputItem.replace(/_/g, ' ')} (have ${totalCount})` };
        }

        // Check for secondary ingredient (vial of water) - inventory first, then BloomHold
        let vialSource = 'inventory';
        const inventoryVialCount = gameState.getItemCount(recipe.secondaryItem);
        const bloomholdVialCount = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(recipe.secondaryItem) : 0;

        if (inventoryVialCount >= recipe.secondaryQuantity) {
            vialSource = 'inventory';
        } else if (bloomholdVialCount >= recipe.secondaryQuantity) {
            vialSource = 'bloomhold';
        } else {
            const totalCount = inventoryVialCount + bloomholdVialCount;
            return { canBrew: false, reason: `Need ${recipe.secondaryQuantity} ${recipe.secondaryItem.replace(/_/g, ' ')} (have ${totalCount})` };
        }

        return { canBrew: true, herbSource, vialSource };
    }

    // Start brewing a potion
    startBrewingPotion(potionId, showToast = true) {
        const brewResult = this.canBrewPotion(potionId);
        if (!brewResult.canBrew) {
            if (showToast) toastWarning(brewResult.reason);
            return false;
        }

        const recipe = Object.values(POTION_RECIPES).find(p => p.id === potionId);
        const speedMultiplier = this.getEffectiveSpeedMultiplier({ subType: 'brewing' });
        const adjustedTime = recipe.baseTime / speedMultiplier;
        const herbSource = brewResult.herbSource;
        const vialSource = brewResult.vialSource;

        // Capture reference to skill for use in tick function
        const skill = this;

        const action = {
            type: SKILLS.ALCHEMY,
            subType: 'potion',
            name: `Brewing ${recipe.name}`,
            recipe: recipe,
            herbSource: herbSource,
            vialSource: vialSource,
            progress: 0,
            duration: adjustedTime,
            startTime: Date.now(),
            forceComplete: false,
            tick: function(gameSpeedMultiplier = 1) {
                // Handle force complete from admin panel
                if (this.forceComplete) {
                    this.forceComplete = false;
                    skill.completeBrewingPotion(recipe, this.herbSource, this.vialSource);
                    // Try to restart if we have materials (no toast on auto-restart)
                    const { canBrew } = skill.canBrewPotion(potionId);
                    if (canBrew) {
                        skill.startBrewingPotion(potionId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - this.startTime) * gameSpeedMultiplier;
                this.progress = Math.min(elapsed / this.duration, 1);

                // Complete when progress reaches 1
                if (this.progress >= 1) {
                    skill.completeBrewingPotion(recipe, this.herbSource, this.vialSource);
                    // Try to restart if we have materials (no toast on auto-restart)
                    const { canBrew } = skill.canBrewPotion(potionId);
                    if (canBrew) {
                        skill.startBrewingPotion(potionId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        const sourceInfo = [];
        if (herbSource === 'bloomhold') sourceInfo.push('herb');
        if (vialSource === 'bloomhold') sourceInfo.push('vial');
        const sourceLog = sourceInfo.length > 0 ? ` (${sourceInfo.join(', ')} from BloomHold)` : '';
        logger.info(`Started brewing ${recipe.name}${sourceLog}`);
        return true;
    }

    // Complete brewing a potion
    completeBrewingPotion(recipe, herbSource = 'inventory', vialSource = 'inventory') {
        // Get output item ID for proc notifications
        const outputItemId = recipe.drops[0]?.itemId;

        // Check for herb conservation proc (Herb Conservation talent - saves clean herb)
        const herbSaved = rollTalentProc(SKILLS.ALCHEMY, 'brewHerbSave');

        // Consume primary input (clean herb) from the appropriate source (unless saved)
        if (!herbSaved) {
            let removedPrimary = false;
            if (herbSource === 'bloomhold') {
                removedPrimary = gameState.removeBloomholdItem(recipe.inputItem, recipe.inputQuantity);
            } else {
                removedPrimary = gameState.removeItem(recipe.inputItem, recipe.inputQuantity);
            }

            if (!removedPrimary) {
                logger.warn('Failed to remove primary ingredient');
                this.stopAction();
                return;
            }
        } else {
            recordTalentProc(SKILLS.ALCHEMY, 'Herb Conservation', 'Herb preserved!', null);
        }

        // Consume secondary input (vial of water) from the appropriate source
        let removedSecondary = false;
        if (vialSource === 'bloomhold') {
            removedSecondary = gameState.removeBloomholdItem(recipe.secondaryItem, recipe.secondaryQuantity);
        } else {
            removedSecondary = gameState.removeItem(recipe.secondaryItem, recipe.secondaryQuantity);
        }

        if (!removedSecondary) {
            // Refund primary ingredient to original source (only if it was consumed)
            if (!herbSaved) {
                if (herbSource === 'bloomhold') {
                    gameState.addBloomholdItem(recipe.inputItem, recipe.inputQuantity);
                } else {
                    gameState.addItem(recipe.inputItem, recipe.inputQuantity);
                }
            }
            logger.warn('Failed to remove secondary ingredient');
            this.stopAction();
            return;
        }

        // Award XP (addXP handles admin multiplier internally)
        const xpResult = addXP(SKILLS.ALCHEMY, recipe.xp, recipe.id);

        // Check for ashes bonus (auto-consume if available, 25% chance for double output)
        let ashesBonus = false;
        if (gameState.getItemCount('ashes') >= 3) {
            gameState.removeItem('ashes', 3);
            // 25% chance for double output
            if (Math.random() < 0.25) {
                ashesBonus = true;
            }
        }

        // Check for double brew talent proc (Double Batch)
        const doubleBrew = rollTalentProc(SKILLS.ALCHEMY, 'doubleBrew');

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.ALCHEMY, recipe.drops);

        // Award bonus drop and XP if ashes proc'd
        if (ashesBonus && outputItemId) {
            awardDrops(SKILLS.ALCHEMY, recipe.drops);
            addXP(SKILLS.ALCHEMY, recipe.xp, recipe.id);
            recordTalentProc(SKILLS.ALCHEMY, 'Ashes Bonus', null, null, { itemId: outputItemId, quantity: 1 });
        }

        // Award bonus drop if talent proc'd
        if (doubleBrew && outputItemId) {
            awardDrops(SKILLS.ALCHEMY, recipe.drops);
            recordTalentProc(SKILLS.ALCHEMY, 'Double Batch', null, null, { itemId: outputItemId, quantity: 1 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.ALCHEMY);

        // Check for pilferer contract assistance (processing)
        pilferingSkill.tryContractAssist('alchemy');

        const sourceInfo = [];
        if (herbSource === 'bloomhold') sourceInfo.push('herb');
        if (vialSource === 'bloomhold') sourceInfo.push('vial');
        const sourceLog = sourceInfo.length > 0 ? ` (${sourceInfo.join(', ')} from BloomHold)` : '';
        const ashesLog = ashesBonus ? ' (Ashes bonus!)' : '';
        logger.game(`Brewed ${recipe.name}! +${recipe.xp} XP${sourceLog}${ashesLog}`);

        if (this.onBrewingComplete) {
            this.onBrewingComplete(recipe, xpResult);
        }
    }

    // Stop current action
    stopAction() {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.ALCHEMY) {
            gameState.clearAction();
            logger.info('Stopped alchemy action');
        }
    }

    // Handle action completion (called from tick)
    handleActionComplete(action) {
        if (!action || !action.recipe) {
            gameState.clearAction();
            return;
        }

        if (action.subType === 'cleaning') {
            // Extract values before completion (action state may change)
            const recipe = action.recipe;
            const recipeId = recipe.id;
            const herbSource = action.herbSource;
            this.completeCleaningHerb(recipe, herbSource);
            // Try to restart if we still have materials (no toast on auto-restart)
            const cleanResult = this.canCleanHerb(recipeId);
            if (cleanResult.canClean) {
                this.startCleaningHerb(recipeId, false);
            } else {
                gameState.clearAction();
            }
        } else if (action.subType === 'potion') {
            // Extract values before completion (action state may change)
            const recipe = action.recipe;
            const recipeId = recipe.id;
            const herbSource = action.herbSource;
            const vialSource = action.vialSource;
            this.completeBrewingPotion(recipe, herbSource, vialSource);
            // Try to restart if we still have materials (no toast on auto-restart)
            const brewResult = this.canBrewPotion(recipeId);
            if (brewResult.canBrew) {
                this.startBrewingPotion(recipeId, false);
            } else {
                gameState.clearAction();
            }
        }
    }

    // Toggle cleaning action
    toggleCleaning(herbId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.ALCHEMY &&
            currentAction?.subType === 'cleaning' &&
            currentAction?.recipe?.id === herbId) {
            this.stopAction();
        } else {
            this.startCleaningHerb(herbId);
        }
    }

    // Toggle brewing action
    toggleBrewing(potionId) {
        const currentAction = gameState.getCurrentAction();
        if (currentAction?.type === SKILLS.ALCHEMY &&
            currentAction?.subType === 'potion' &&
            currentAction?.recipe?.id === potionId) {
            this.stopAction();
        } else {
            this.startBrewingPotion(potionId);
        }
    }
}

export const alchemySkill = new AlchemySkill();
